/* eslint semi: 1 */
import { computed, onMounted, watch } from 'vue';
import { TARGET_COMPONENTS } from '@/composables/file-upload/use-file-uploader';
import { useFileUploadStorage } from '@/composables/file-upload/use-file-upload-storage';
import { useFileUploadProgressStore } from '@/pinia/stores/file-upload/fileUploadProgressStore';
import CONST from '@/globals/constants';
import type { FileUploadItem, FileUploadItemStatus } from '@base-shared/types/file-uploader';

type TargetComponentType = typeof TARGET_COMPONENTS[keyof typeof TARGET_COMPONENTS];

/**
 * Composable for managing file upload progress tracking.
 * Monitors uploads for specific target components and synchronizes state with the progress store.
 *
 * Features:
 * - Filters active uploads by target component type
 * - Detects when all uploads are finished
 * - Provides methods to control popup visibility and target components
 * - Automatically syncs storage updates to progress store
 *
 * @returns {Object} Upload progress state and control methods
 */
export function useFileUploaderProgress () {
  const fileUploadStorage = useFileUploadStorage();
  const progressStore = useFileUploadProgressStore();

  /** Whether the upload progress popup is currently visible */
  const isPopupVisible = computed(() => progressStore.isPopupVisible);

  /** Component types whose uploads are being tracked */
  const targetComponents = computed(() => progressStore.trackedComponents);

  /** Files currently being uploaded for tracked components */
  const activeUploads = computed(() => progressStore.trackedUploadFiles);

  /**
   * Filters storage files to only include those matching tracked components.
   * Returns empty array if no components are being tracked.
   */
  const filesForTrackedComponents = computed(() => {
    if (!targetComponents.value.length) return [];

    return fileUploadStorage.files.value.filter((file) =>
      targetComponents.value.includes((file._edit?.targetComponent ?? '') as TargetComponentType)
    );
  });

  /**
   * Checks if all tracked uploads have completed.
   * Considers both 'finished', 'failed' status or 100%
   * progress as completion indicators.
   */
  const allActiveUploadsDone = computed(() => {
    if (!activeUploads.value?.length) return false;
    return activeUploads.value.every((file) =>
      [
        CONST.FILE_UPLOAD_ITEM_STATUS.FINISH,
        CONST.FILE_UPLOAD_ITEM_STATUS.FINISHED,
        CONST.FILE_UPLOAD_ITEM_STATUS.FAILED,
        CONST.FILE_UPLOAD_ITEM_STATUS.CANCEL
      ].includes(file.status as FileUploadItemStatus)
    );
  });

  const allActiveUploadsSucceeded = computed(() => {
    if (!allActiveUploadsDone.value) return false;
    return activeUploads.value.every((file) =>
      [
        CONST.FILE_UPLOAD_ITEM_STATUS.FINISH,
        CONST.FILE_UPLOAD_ITEM_STATUS.FINISHED
      ].includes(file.status as FileUploadItemStatus));
  });

  const allActiveUploadsFailed = computed(() => {
    if (!allActiveUploadsDone.value) return false;
    return activeUploads.value.every((file) => [
      CONST.FILE_UPLOAD_ITEM_STATUS.FAILED,
      CONST.FILE_UPLOAD_ITEM_STATUS.CANCEL
    ].includes(file.status as FileUploadItemStatus));
  });

  /** Watch for changes in filtered files and sync with progress store */
  watch(filesForTrackedComponents, (newFiles) => syncFilesToProgressStore(newFiles), { deep: true });

  /**
   * Synchronizes storage files with the progress store.
   * Handles three scenarios: removed files, updated files, and new files.
   *
   * 1. Mark removed files as finished
   * 2. Update existing files with new data
   * 3. Keep finalized uploads separate from new uploads
   * @param {FileUploadItem[]} currentStorageFiles - Current files from storage
   */
  function syncFilesToProgressStore (currentStorageFiles: FileUploadItem[]) {
    const currentTrackedFiles = progressStore.trackedUploadFiles;

    // Find files that no longer exist in storage (removed/completed)
    const removedFiles = currentTrackedFiles.filter((trackedFile) =>
      !currentStorageFiles.some((storageFile) => storageFile.uid === trackedFile.uid)
    );

    // Find files that are new in storage
    const newFiles = currentStorageFiles.filter((storageFile) =>
      !currentTrackedFiles.some((trackedFile) => trackedFile.uid === storageFile.uid)
    );

    // Update existing tracked files with latest data from storage
    const updatedTrackedFiles = currentTrackedFiles.map((trackedFile) => {
      // Mark removed files as finished
      const removedFile = removedFiles.find((removedFile) => removedFile.uid === trackedFile.uid);
      if (removedFile) {
        return {
          ...trackedFile,
          status: [CONST.FILE_UPLOAD_ITEM_STATUS.FAILED, CONST.FILE_UPLOAD_ITEM_STATUS.CANCEL].includes(removedFile.status as FileUploadItemStatus)
            ? removedFile.status as FileUploadItemStatus
            : CONST.FILE_UPLOAD_ITEM_STATUS.FINISHED as FileUploadItemStatus,
          percentage: 100
        };
      }

      // Replace with updated data from storage, or keep existing
      const updatedFile = currentStorageFiles.find((f) => f.uid === trackedFile.uid);
      return updatedFile ?? trackedFile;
    });

    // If all uploads are done and new files exist, reset to show fresh uploads
    if (allActiveUploadsDone.value && newFiles.length) {
      progressStore.setTrackedUploadFiles(newFiles);
      return;
    }

    // Merge updated files with new files
    progressStore.setTrackedUploadFiles([...updatedTrackedFiles, ...newFiles]);
  }

  /**
   * Update which component types should have their uploads tracked.
   * Only updates if components have actually changed.
   * Shows popup when components are updated.
   *
   * @param {TargetComponentType[]} components - Component types to track
   */
  function setTargetComponents (components: TargetComponentType[]) {
    const hasChanged = (
      components.length !== targetComponents.value.length ||
      !components.every((comp, index) => comp === targetComponents.value[index])
    );

    if (!hasChanged) return;

    progressStore.setTrackedComponents(components);
    progressStore.setTrackedUploadFiles(filesForTrackedComponents.value);
    progressStore.setPopupVisible(true);
  }

  /**
   * Control visibility of the upload progress popup.
   * @param {boolean} isVisible - Whether popup should be visible
   */
  function setPopupVisible (isVisible: boolean) {
    progressStore.setPopupVisible(isVisible);
  }

  /**
   * Close the popup and clear all tracked uploads.
   * Used when user confirms they've seen the upload completion.
   */
  function dismissPopUp () {
    progressStore.setTrackedUploadFiles([]);
    setPopupVisible(true);
  }

  /**
   * Reset all upload progress state to defaults.
   * Typically called during user sign-out.
   */
  function clearState () {
    progressStore.reset();
  }

  onMounted(() => {
    // Sync initial storage files to store on component mount
    fileUploadStorage.getFiles();
  });

  return {
    // State
    isPopupVisible,
    targetComponents,
    activeUploads,
    allActiveUploadsDone,
    allActiveUploadsSucceeded,
    allActiveUploadsFailed,

    // Methods
    setTargetComponents,
    setPopupVisible,
    dismissPopUp,
    clearState
  };
}
